import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaArrowLeft as FaArrowLeftRaw,
  FaBolt as FaBoltRaw,
  FaBook as FaBookRaw,
  FaBullhorn as FaBullhornRaw,
  FaCalendarAlt as FaCalendarAltRaw,
  FaChartBar as FaChartBarRaw,
  FaChevronDown as FaChevronDownRaw,
  FaChevronRight as FaChevronRightRaw,
  FaClipboardList as FaClipboardListRaw,
  FaComment as FaCommentRaw,
  FaCog as FaCogRaw,
  FaEnvelope as FaEnvelopeRaw,
  FaFileAlt as FaFileAltRaw,
  FaHeart as FaHeartRaw,
  FaPlus as FaPlusRaw,
  FaSignal as FaSignalRaw,
  FaUserFriends as FaUserFriendsRaw,
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import {
  teacherService,
  type TeacherAnnouncement,
  type TeacherGradeRow,
  type TeacherLesson,
  type TeacherWorkItem,
} from '../../services/classroom/teacherService';
import {
  LessonBlockRenderer,
  parseLessonStoredDescription,
} from '../../components/classroom/LessonBlockRenderer';
import ClassSectionSidebar from '../../components/classroom/ClassSectionSidebar';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaArrowLeft: any = FaArrowLeftRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaBolt: any = FaBoltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaBook: any = FaBookRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaBullhorn: any = FaBullhornRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCalendarAlt: any = FaCalendarAltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChartBar: any = FaChartBarRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronDown: any = FaChevronDownRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronRight: any = FaChevronRightRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaClipboardList: any = FaClipboardListRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaComment: any = FaCommentRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCog: any = FaCogRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEnvelope: any = FaEnvelopeRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFileAlt: any = FaFileAltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaHeart: any = FaHeartRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPlus: any = FaPlusRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSignal: any = FaSignalRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUserFriends: any = FaUserFriendsRaw;

const MOBILE_BREAKPOINT = '1024px';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const BackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Hero = styled.section<{ $imageUrl?: string | null }>`
  border-radius: 16px;
  border: 1px solid var(--outline);
  padding: 1.5rem 1.65rem 1.35rem;
  color: var(--onPrimary);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.1);
  background: ${({ $imageUrl }) => {
    const base = 'linear-gradient(135deg, var(--primaryDark) 0%, var(--primary) 55%, color-mix(in srgb, var(--primary) 70%, var(--surface)) 100%)';
    const url = typeof $imageUrl === 'string' ? $imageUrl.trim() : '';
    if (!url) return base;
    return `linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.15) 100%), url(${url}) center / cover no-repeat, ${base}`;
  }};
`;

const Breadcrumb = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.88;
`;

const Title = styled.h1`
  margin: 0 0 1rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.45rem, 3vw, 2rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
`;

const HeroStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const HeroPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(6px);
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) minmax(0, 280px);
  gap: 1.25rem;
  align-items: start;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

const SettingsToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.55rem;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0.55rem 0.75rem;
  margin: 0.1rem 0;
  border-radius: 10px;
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.86rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  &:hover {
    background: var(--surfaceVariant);
    color: var(--text);
  }
`;

const SettingsLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
`;

const SettingsItems = styled.div`
  display: grid;
  gap: 0.1rem;
  margin: 0.1rem 0 0.2rem;
  padding-left: 0.75rem;
`;

const SettingsItemLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  margin: 0.1rem 0;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
  text-decoration: none;
  &:hover {
    background: var(--surfaceVariant);
    color: var(--text);
    border-color: var(--outline);
  }
`;

const DangerSideButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  margin: 0.1rem 0;
  font-size: 0.86rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--error, #dc2626);
  text-align: left;
  cursor: pointer;
  &:hover {
    background: color-mix(in srgb, var(--error, #dc2626) 8%, transparent);
    border-color: color-mix(in srgb, var(--error, #dc2626) 30%, transparent);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const FeedColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
`;

const FeedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
`;

const FeedIcon = styled.span`
  display: inline-flex;
  color: var(--primary);
`;

const Card = styled.article`
  border: 1px solid var(--outline);
  border-radius: 14px;
  background: var(--surface);
  padding: 1rem 1.1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
`;

const AnnouncementTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 999px;
  background: var(--primaryContainer, var(--surfaceVariant));
  color: var(--primary);
  font-size: 0.8rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid var(--outline);
`;

const AnnouncementMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const PosterLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.65rem;
`;

const PosterName = styled.span`
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--text);
`;

const PostedTime = styled.span`
  font-size: 0.78rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const TagAnnouncement = styled.span`
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 0.2rem 0.45rem;
  border-radius: 6px;
  background: color-mix(in srgb, var(--warning, #e8a23c) 22%, transparent);
  color: var(--warning, #b45309);
`;

const AnnouncementTitle = styled.h3`
  margin: 0.35rem 0 0.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text);
`;

const AnnouncementBody = styled.p`
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const CardFooterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.85rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--outline);
  font-size: 0.8rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const FooterStat = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const DeadlineCard = styled(Card)`
  border-left: 4px solid var(--error, #dc2626);
`;

const DeadlineHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
`;

const DeadlineIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--error, #dc2626) 12%, transparent);
  color: var(--error, #dc2626);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const DeadlineTitle = styled.h3`
  margin: 0 0 0.35rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text);
`;

const DeadlineDesc = styled.p`
  margin: 0;
  font-size: 0.84rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.45;
`;

const DeadlineFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.85rem;
`;

const DueSoon = styled.span`
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--error, #dc2626);
`;

const TextLink = styled(Link)`
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--primary);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ResourcePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.65rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text);
`;

const ResourceToggle = styled.button`
  margin-top: 0.65rem;
  display: inline-flex;
  align-items: center;
  gap: 0.32rem;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.28rem 0.52rem;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const ResourceBlocks = styled.div`
  display: grid;
  gap: 0.6rem;
  margin-top: 0.7rem;
`;

const RightCol = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
  position: sticky;
  top: 1rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    position: static;
  }
`;

const ActionsCard = styled.div`
  border-radius: 16px;
  padding: 0.85rem 0.7rem 0.95rem;
  box-sizing: border-box;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.45rem;
  background: linear-gradient(155deg, var(--primaryDark) 0%, var(--primary) 100%);
  color: var(--onPrimary);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
`;

const ActionsCardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 0.88rem;
  margin-bottom: 0.15rem;
  min-width: 0;
`;

const ActionBtnBase = styled(Link)`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.35rem 0.45rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 0.5rem 0.55rem;
  border-radius: 10px;
  text-align: center;
  text-decoration: none;
  line-height: 1.25;
  word-break: break-word;
  hyphens: auto;
`;

const PrimaryActionBtn = styled(ActionBtnBase)`
  margin: 0;
  border: none;
  background: var(--onPrimary);
  color: var(--primary);
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    filter: brightness(0.97);
  }
`;

const SecondaryActionBtn = styled(ActionBtnBase)`
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.12);
  color: var(--onPrimary);
  font-size: 0.78rem;
  font-weight: 600;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const AttentionCard = styled(Card)`
  padding: 1rem;
`;

const AttentionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--text);
  margin-bottom: 0.75rem;
`;

const AttentionIcon = styled.span`
  color: var(--error, #dc2626);
`;

const AttentionRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.65rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid var(--outline);
  &:last-of-type {
    border-bottom: none;
  }
`;

const AttentionName = styled.div`
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--text);
`;

const AttentionIssue = styled.div<{ $tone: 'danger' | 'warn' }>`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  margin-top: 0.2rem;
  color: ${({ $tone }) => ($tone === 'danger' ? 'var(--error, #dc2626)' : 'var(--warning, #d97706)')};
`;

const IconBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid var(--outline);
  color: var(--textSecondary, var(--onSurfaceVariant));
  flex-shrink: 0;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const AttentionFooter = styled.div`
  margin-top: 0.5rem;
  padding-top: 0.65rem;
  border-top: 1px solid var(--outline);
`;

const Hint = styled.p`
  margin: 0;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const EmptyFeed = styled.p`
  margin: 0;
  padding: 1.25rem;
  text-align: center;
  font-size: 0.88rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  border: 1px dashed var(--outline);
  border-radius: 14px;
  background: var(--surfaceVariant);
`;

function initials(name: string): string {
  const p = name.split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return `${p[0]!.charAt(0)}${p[1]!.charAt(0)}`.toUpperCase();
}

function formatRelativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Recently';
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function dueInLabel(iso: string | null): string {
  if (!iso) return 'No due date';
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return 'Due soon';
  const ms = end - Date.now();
  if (ms < 0) return 'Overdue';
  const hr = Math.floor(ms / (1000 * 60 * 60));
  if (hr < 1) {
    const m = Math.max(1, Math.floor(ms / (1000 * 60)));
    return `Due in ${m} min`;
  }
  if (hr < 48) return `Due in ${hr} hour${hr === 1 ? '' : 's'}`;
  const d = Math.floor(hr / 24);
  return `Due in ${d} days`;
}

function classCodeLabel(classId: string, className: string): string {
  const short = classId.replace(/-/g, '').slice(0, 6).toUpperCase();
  const topic = className.split(/\s+/).slice(0, 2).join(' ').toUpperCase();
  return `${topic || 'CLASS'} · ${short || 'NEW'}`;
}

function mergeUpcomingWork(assignments: TeacherWorkItem[], quizzes: TeacherWorkItem[], cid: string): TeacherWorkItem[] {
  const rows = [...assignments, ...quizzes].filter((w) => {
    if (w.class_id !== cid || w.archived) return false;
    if (!w.due_at) return false;
    const t = new Date(w.due_at).getTime();
    return !Number.isNaN(t) && t >= Date.now();
  });
  rows.sort((a, b) => new Date(a.due_at!).getTime() - new Date(b.due_at!).getTime());
  return rows;
}

function filterGradesForClass(rows: TeacherGradeRow[], cid: string, className: string): TeacherGradeRow[] {
  const classRows = rows.filter((g) => g.class_id === cid || (!g.class_id && g.class_name === className));
  const latestByStudentAndWork = new Map<string, TeacherGradeRow>();
  const passthrough: TeacherGradeRow[] = [];

  const statusPriority = (status: TeacherGradeRow['status']): number => {
    if (status === 'graded' || status === 'returned') return 3;
    if (status === 'submitted') return 2;
    return 1;
  };
  const rowTimestamp = (value?: string | null): number => {
    if (!value) return 0;
    const ts = new Date(value).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  };

  classRows.forEach((row) => {
    const workType = row.work_type ?? 'assignment';
    const workKey = row.work_id?.trim()
      ? `${workType}:id:${row.work_id.trim()}`
      : row.work_title.trim()
        ? `${workType}:title:${row.work_title.trim().toLowerCase()}`
        : '';
    const studentKey = row.student_id?.trim()
      ? `id:${row.student_id.trim()}`
      : row.student_name.trim()
        ? `name:${row.student_name.trim().toLowerCase()}`
        : '';
    if (!workKey || !studentKey) {
      passthrough.push(row);
      return;
    }
    const key = `${workKey}::${studentKey}`;
    const prev = latestByStudentAndWork.get(key);
    if (!prev) {
      latestByStudentAndWork.set(key, row);
      return;
    }
    const prevIsMissing = prev.status === 'missing';
    const currIsMissing = row.status === 'missing';
    if (prevIsMissing !== currIsMissing) {
      latestByStudentAndWork.set(key, currIsMissing ? prev : row);
      return;
    }
    const prevTs = rowTimestamp(prev.submitted_at);
    const currTs = rowTimestamp(row.submitted_at);
    if (currTs > prevTs) {
      latestByStudentAndWork.set(key, row);
      return;
    }
    if (currTs === prevTs && statusPriority(row.status) > statusPriority(prev.status)) {
      latestByStudentAndWork.set(key, row);
    }
  });

  return Array.from(latestByStudentAndWork)
    .map(([, row]) => row)
    .concat(passthrough);
}

const ClassroomTeacherClassDetailPage: React.FC = () => {
  const { classId = '' } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);

  const displayName =
    profile?.full_name?.trim() ||
    (typeof user?.user_metadata?.full_name === 'string' && user.user_metadata.full_name.trim()) ||
    user?.email?.split('@')[0] ||
    'Teacher';
  const [resourceExpanded, setResourceExpanded] = useState(false);
  const [deletingClass, setDeletingClass] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [
    overviewQuery,
    lessonsQuery,
    assignmentsQuery,
    quizzesQuery,
    gradesQuery,
    announcementsQuery,
  ] = useQueries({
    queries: [
      { queryKey: ['classroom', 'teacher', 'overview'], queryFn: teacherService.getOverview },
      { queryKey: ['classroom', 'teacher', 'lessons'], queryFn: teacherService.getLessons },
      { queryKey: ['classroom', 'teacher', 'work', 'assignment'], queryFn: () => teacherService.getWork('assignment') },
      { queryKey: ['classroom', 'teacher', 'work', 'quiz'], queryFn: () => teacherService.getWork('quiz') },
      { queryKey: ['classroom', 'teacher', 'grades'], queryFn: teacherService.getGrades },
      { queryKey: ['classroom', 'teacher', 'announcements'], queryFn: teacherService.getAnnouncements },
    ],
  });

  const loading =
    overviewQuery.isLoading ||
    lessonsQuery.isLoading ||
    assignmentsQuery.isLoading ||
    quizzesQuery.isLoading ||
    gradesQuery.isLoading ||
    announcementsQuery.isLoading;
  const hasError =
    overviewQuery.isError ||
    lessonsQuery.isError ||
    assignmentsQuery.isError ||
    quizzesQuery.isError ||
    gradesQuery.isError ||
    announcementsQuery.isError;

  const classItem = useMemo(
    () => (overviewQuery.data ?? []).find((row) => row.id === classId),
    [overviewQuery.data, classId]
  );

  const classLessons = useMemo(
    () => (lessonsQuery.data ?? []).filter((row) => row.class_id === classId).sort((a, b) => a.sequence - b.sequence),
    [lessonsQuery.data, classId]
  );

  const classAssignments = useMemo(
    () => (assignmentsQuery.data ?? []).filter((row) => row.class_id === classId),
    [assignmentsQuery.data, classId]
  );

  const classQuizzes = useMemo(
    () => (quizzesQuery.data ?? []).filter((row) => row.class_id === classId),
    [quizzesQuery.data, classId]
  );

  const classGrades = useMemo(() => {
    if (!classItem) return [];
    return filterGradesForClass(gradesQuery.data ?? [], classId, classItem.name);
  }, [gradesQuery.data, classId, classItem]);

  const announcementsForClass = useMemo(() => {
    const rows = announcementsQuery.data ?? [];
    return rows
      .filter((a: TeacherAnnouncement) => a.class_id === classId || a.class_id == null)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 4);
  }, [announcementsQuery.data, classId]);

  const upcomingWork = useMemo(
    () => mergeUpcomingWork(assignmentsQuery.data ?? [], quizzesQuery.data ?? [], classId).slice(0, 3),
    [assignmentsQuery.data, quizzesQuery.data, classId]
  );

  const attentionRows = useMemo(() => {
    const missingByStudent = new Map<string, number>();
    classGrades
      .filter((g) => g.status === 'missing')
      .forEach((g) => missingByStudent.set(g.student_name, (missingByStudent.get(g.student_name) ?? 0) + 1));

    const lowQuiz = new Map<string, number>();
    classGrades.forEach((g) => {
      if (g.work_type !== 'quiz' || g.score == null || g.score >= 72) return;
      const prev = lowQuiz.get(g.student_name);
      if (prev == null || g.score < prev) lowQuiz.set(g.student_name, g.score);
    });

    type Row = { name: string; issue: string; tone: 'danger' | 'warn' };
    const out: Row[] = [];
    missingByStudent.forEach((count, name) => {
      out.push({
        name,
        issue: `MISSING ${count} ASSIGNMENT${count === 1 ? '' : 'S'}`,
        tone: 'danger',
      });
    });
    lowQuiz.forEach((score, name) => {
      if (missingByStudent.has(name)) return;
      out.push({
        name,
        issue: `LOW QUIZ AVERAGE (${Math.round(score)}%)`,
        tone: 'warn',
      });
    });
    return out.slice(0, 5);
  }, [classGrades]);

  const resourceLesson: TeacherLesson | undefined = classLessons[0];
  const resourceLessonParsed = useMemo(
    () => parseLessonStoredDescription(resourceLesson?.description),
    [resourceLesson?.description]
  );

  if (loading) return <Hint>Loading class details…</Hint>;
  if (hasError) return <Hint>Unable to load class details.</Hint>;
  if (!classItem) {
    return (
      <Page>
        <BackRow>
          <BackLink to="/classroom-teacher/classes">
            <FaArrowLeft size={12} aria-hidden />
            Back to Classes
          </BackLink>
        </BackRow>
        <Hint>Class not found.</Hint>
      </Page>
    );
  }

  const assignmentCount = classAssignments.length + classQuizzes.length;
  const shortClassName = classItem.name.split(/\s+/).slice(0, 3).join(' ') || 'This class';

  const handleDeleteClass = async () => {
    if (deletingClass) return;
    const confirmed = window.confirm(
      `Delete "${classItem.name}"?\n\nThis will permanently remove the class and all related classroom data. This action cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingClass(true);
    const result = await teacherService.deleteClass(classId);
    if (!result.ok) {
      window.alert(result.error || 'Unable to delete class.');
      setDeletingClass(false);
      return;
    }

    navigate('/classroom-teacher/classes');
  };

  return (
    <Page>
      <BackRow>
        <BackLink to="/classroom-teacher/classes">
          <FaArrowLeft size={12} aria-hidden />
          Back to Classes
        </BackLink>
      </BackRow>

      <Hero $imageUrl={classItem.class_image_url}>
        <Breadcrumb>{classCodeLabel(classId, classItem.name)}</Breadcrumb>
        <Title>{classItem.name}</Title>
        <HeroStats>
          <HeroPill>
            <FaUserFriends size={14} aria-hidden />
            {classItem.activeStudents} Students
          </HeroPill>
          <HeroPill>
            <FaBook size={14} aria-hidden />
            {classLessons.length} Lessons
          </HeroPill>
          <HeroPill>
            <FaClipboardList size={14} aria-hidden />
            {assignmentCount} Assignments
          </HeroPill>
        </HeroStats>
      </Hero>

      <LayoutGrid>
        <ClassSectionSidebar classId={classId} className={shortClassName} showSettingsLink={false}>
          <SettingsToggle type="button" aria-expanded={settingsOpen} onClick={() => setSettingsOpen((v) => !v)}>
            <SettingsLabel>
              <FaCog size={14} aria-hidden />
              Settings
            </SettingsLabel>
            {settingsOpen ? <FaChevronDown size={11} aria-hidden /> : <FaChevronRight size={11} aria-hidden />}
          </SettingsToggle>
          {settingsOpen ? (
            <SettingsItems>
              <SettingsItemLink to={`/classroom-teacher/classes/${encodeURIComponent(classId)}/edit`}>
                <FaCog size={13} aria-hidden />
                Edit Class
              </SettingsItemLink>
              <DangerSideButton type="button" onClick={handleDeleteClass} disabled={deletingClass}>
                <FaCog size={13} aria-hidden />
                {deletingClass ? 'Deleting class…' : 'Delete class'}
              </DangerSideButton>
            </SettingsItems>
          ) : null}
        </ClassSectionSidebar>

        <FeedColumn>
          <FeedHeader>
            <FeedIcon>
              <FaSignal size={18} aria-hidden />
            </FeedIcon>
            Class Feed
          </FeedHeader>

          {announcementsForClass.length === 0 && upcomingWork.length === 0 && !resourceLesson ? (
            <EmptyFeed>No announcements or upcoming deadlines yet. Post an update or add assignments.</EmptyFeed>
          ) : null}

          {announcementsForClass.map((a) => (
            <Card key={a.id}>
              <AnnouncementTop>
                <Avatar aria-hidden>{initials(displayName)}</Avatar>
                <AnnouncementMeta>
                  <PosterLine>
                    <PosterName>{displayName}</PosterName>
                    <PostedTime>Posted {formatRelativeTime(a.created_at)}</PostedTime>
                    <TagAnnouncement>ANNOUNCEMENT</TagAnnouncement>
                  </PosterLine>
                  <AnnouncementTitle>{a.title}</AnnouncementTitle>
                  <AnnouncementBody>{a.body}</AnnouncementBody>
                </AnnouncementMeta>
              </AnnouncementTop>
              <CardFooterRow>
                <FooterStat>
                  <FaComment size={13} aria-hidden />
                  Comments
                </FooterStat>
                <FooterStat>
                  <FaHeart size={13} aria-hidden />
                  Activity
                </FooterStat>
              </CardFooterRow>
            </Card>
          ))}

          {upcomingWork.map((w) => (
            <DeadlineCard key={`${w.type}-${w.id}`}>
              <DeadlineHead>
                <DeadlineIcon>
                  <FaCalendarAlt size={16} aria-hidden />
                </DeadlineIcon>
                <div style={{ minWidth: 0 }}>
                  <DeadlineTitle>
                    Upcoming deadline: {w.title}
                    {w.type === 'quiz' ? ' (Quiz)' : ''}
                  </DeadlineTitle>
                  <DeadlineDesc>
                    {w.points ? `${w.points} pts · ` : ''}
                    Keep students on track—review submissions from the grading queue.
                  </DeadlineDesc>
                </div>
              </DeadlineHead>
              <DeadlineFooter>
                <DueSoon>{dueInLabel(w.due_at)}</DueSoon>
                <TextLink
                  to={
                    w.type === 'quiz'
                      ? `/classroom-teacher/quizzes/${encodeURIComponent(w.id)}/edit`
                      : `/classroom-teacher/assignments/${encodeURIComponent(w.id)}`
                  }
                >
                  {w.type === 'quiz' ? 'View quiz' : 'View assignment'}
                </TextLink>
              </DeadlineFooter>
            </DeadlineCard>
          ))}

          {resourceLesson ? (
            <Card>
              <AnnouncementTop>
                <DeadlineIcon style={{ background: 'var(--surfaceVariant)', color: 'var(--primary)' }}>
                  <FaFileAlt size={15} aria-hidden />
                </DeadlineIcon>
                <AnnouncementMeta>
                  <PosterLine>
                    <PosterName>New resource</PosterName>
                    <PostedTime>Lesson sequence {resourceLesson.sequence}</PostedTime>
                  </PosterLine>
                  <AnnouncementTitle>{resourceLesson.title}</AnnouncementTitle>
                  <AnnouncementBody>
                    {resourceLessonParsed.plainDescription || 'Materials linked to this lesson will appear here for your class.'}
                  </AnnouncementBody>
                </AnnouncementMeta>
              </AnnouncementTop>
              <ResourcePill>
                <FaFileAlt size={12} aria-hidden />
                Class material
              </ResourcePill>
              <ResourceToggle type="button" aria-expanded={resourceExpanded} onClick={() => setResourceExpanded((v) => !v)}>
                {resourceExpanded ? <FaChevronDown size={10} aria-hidden /> : <FaChevronRight size={10} aria-hidden />}
                {resourceExpanded ? 'Hide blocks' : 'Show blocks'}
              </ResourceToggle>
              {resourceExpanded ? (
                resourceLessonParsed.blocks.length > 0 ? (
                  <ResourceBlocks>
                    <LessonBlockRenderer
                      blocks={resourceLessonParsed.blocks}
                      quizzes={classQuizzes}
                      assignments={classAssignments}
                      classId={classId}
                    />
                  </ResourceBlocks>
                ) : (
                  <AnnouncementBody style={{ marginTop: '0.65rem' }}>
                    No content blocks yet. Open this lesson in the lesson editor to add block content.
                  </AnnouncementBody>
                )
              ) : null}
            </Card>
          ) : null}
        </FeedColumn>

        <RightCol>
          <ActionsCard>
            <ActionsCardHead>
              <FaBolt size={16} aria-hidden />
              Class actions
            </ActionsCardHead>
            <PrimaryActionBtn to="/classroom-teacher/communication">
              <FaBullhorn size={14} aria-hidden />
              Create announcement
            </PrimaryActionBtn>
            <SecondaryActionBtn to={`/classroom-teacher/classes/${encodeURIComponent(classId)}/assignments`}>
              <FaPlus size={14} aria-hidden />
              Add resource
            </SecondaryActionBtn>
          </ActionsCard>

          <AttentionCard>
            <AttentionHead>
              <AttentionIcon>
                <FaChartBar size={16} aria-hidden />
              </AttentionIcon>
              Attention needed
            </AttentionHead>
            {attentionRows.length === 0 ? (
              <Hint>No at-risk students flagged for this class yet.</Hint>
            ) : (
              attentionRows.map((row) => (
                <AttentionRow key={`${row.name}-${row.issue}`}>
                  <div style={{ minWidth: 0 }}>
                    <AttentionName>{row.name}</AttentionName>
                    <AttentionIssue $tone={row.tone}>{row.issue}</AttentionIssue>
                  </div>
                  <IconBtn to="/classroom-teacher/communication" title="Message" aria-label={`Message ${row.name}`}>
                    <FaEnvelope size={13} aria-hidden />
                  </IconBtn>
                </AttentionRow>
              ))
            )}
            <AttentionFooter>
              <TextLink to="/classroom-teacher/analytics">View class performance</TextLink>
            </AttentionFooter>
          </AttentionCard>
        </RightCol>
      </LayoutGrid>
    </Page>
  );
};

export default ClassroomTeacherClassDetailPage;
